import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom'

import RoiPCCost from '@/features/roi/ProductContrast/Cost'
import RoiPCLayout from '@/features/roi/ProductContrast/Layout'
import RoiPCProfit from '@/features/roi/ProductContrast/Profit'
import RoiPCRevenue from '@/features/roi/ProductContrast/Revenue'
import RoiPCSummary from '@/features/roi/ProductContrast/Summary'
import RoiCCCost from '@/features/roi/CountryContrast/Cost'
import RoiCCLayout from '@/features/roi/CountryContrast/Layout'
import RoiCCProfit from '@/features/roi/CountryContrast/Profit'
import RoiCCRevenue from '@/features/roi/CountryContrast/Revenue'
import RoiHome from '@/features/roi/Home'
import RoiCountryDetail from '@/features/roi/Home/CountryContrast'
import RoiLayout from './features/roi/Layout'

import RoiWeekly from '@/features/roi/Weekly'
import RoiWeeklyDoc from '@/features/roi/WeeklyDoc'
import RoiMonthly from '@/features/roi/Monthly'
import MonthlyGoals from '@/features/roi/Monthly/goals'
import RoiJPGroup from '@/features/roi/JPGroup'

import RoasLayout from '@/features/roas'
import RoasPage from '@/features/roas/dashboard/total-roas-page'
import RoasDataDashboard from '@/features/roas/dashboard/data-dashboard'
import CampsPage from '@/features/roas/campaigns/campaign-list-page'
import RoasDetail from '@/features/roas/campaigns/campaign-detail-page'
import NotesPage from '@/features/roas/notes/notes-list-page'
import RevenueStructure from '@/features/roas/reports/revenue-structure-page'
import RevenueAgeStructure from '@/features/roas/reports/revenue-age-structure-page'
import RoasSummary from '@/features/roas/dashboard/summary-page'
import RoasReport from '@/features/roas/reports/standard-report-page'
import RoasDetailReport from '@/features/roas/reports/standard-report-detail-page'
import Day0RoasPage from '@/features/roas/reports/dayn-roas-page'
import DayRoas14Page from '@/features/roas/reports/dayn-roas-detail-page'
import RevenuesSnapshot from '@/features/roas/reports/revenue-snapshot-page'
import RecoveryModel from '@/features/roas/recovery/recovery-page'
import RequireCeo from '@/features/ceo/require-ceo'
import CeoPayback from '@/features/ceo/payback-page'
import { ROAS_PAYBACK_PATH } from '@/constants/ceo'
import AdminLayout from '@/features/admin'
import SetRoasReadable from '@/features/admin/SetRoasReadable'
import SetCostReadable from '@/features/admin/SetCostReadable'
import SetPrReadable from '@/features/admin/SetPrReadable'
import SetRevenueReadable from '@/features/admin/SetRevenueReadable'
import SetCamproiReadable from '@/features/admin/SetCamproiReadable'
import SetRoles from '@/features/admin/SetRole'
import UserLayout from '@/features/admin/UserLayout'
import ApiTiming from '@/features/admin/ApiTiming'
import UsersPage from '@/features/admin/UsersPage'
import BotsPage from '@/features/admin/BotsPage'
import PackageAliasPage from '@/features/admin/PackageAliasPage'
import ProductPackagesLoader from '@/components/product-packages-loader'
import ProductTreeLoader from '@/components/product-tree-loader'
import ProductTreePage from '@/features/admin/ProductTreePage'
import AppleEvents from '@/features/apple/Events1'
import Event1Roadmap from '@/features/apple/Events1Roadmap'
import AppleEvents2 from '@/features/apple/Events2'
import Event2Roadmap from '@/features/apple/Events2Roadmap'
import AppleLayout from '@/features/apple/Layout'
import SubAtTimeline from '@/features/apple/SubAtTimeline'
import WhatHappenAfterSubAt from '@/features/apple/WhatHappenAfterSubAt'
import AppleRegression from '@/features/roi/AppleRegression'

import EarningRate from '@/features/er'
import Day0RoasBaseline from '@/features/dr'
import CostBaseline from '@/features/cost-baseline'
import ProfitBaseline from '@/features/profit-baseline'
import Baseline from '@/features/baseline'

import Nav from '@/components/Nav'

import LoginPage from '@/features/auth/FeishuLogin'

import Page401 from '@/components/zthd/401'
import Page403 from '@/components/zthd/403'
import Page404 from '@/components/zthd/404'
import Page500 from '@/components/zthd/500'
import { useAuthStore } from '@/hooks'
import { useRoles } from './hooks/auth'
import { Role } from '@/constants/role'
import { useWindowSize } from './hooks/use-media-query'
import { Toaster } from '@/components/ui/sonner'
import RoiCountryDailyLayout from '@/features/roi/country-daily/Layout'
import { useEffect } from 'react'
import useSWRMutation from 'swr/mutation'
import { postFetcher } from '@/hooks'
import { shouldClearCache } from '@/lib/cache-utils'
import { clearCache } from '@/components/Nav'
import RoiCDRevenue from '@/features/roi/country-daily/Revenue'
import SubscribeSummary from '@/features/apple/SubscribeSummary'
import SetAdmobReadable from './features/admin/SetAdmobReadable'
import SetPamReadable from './features/admin/SetPamReadable'
import AdmobLayout from './features/admob'
import AdmobReportPage from './features/admob/AdmobReportPage'
import PamIndex from './features/admob/pam'
import PamAutoImportPage from './features/admob/pam/PamAutoImportPage'
import PamCreatePage from './features/admob/pam/PamCreatePage'

const Welcome = () => {
  const size = useWindowSize()
  return (
    <div>
      <h1>Welcome to monday. </h1>
      <p>
        {size[1]} x{size[0]}
      </p>
    </div>
  )
}

const LegacyPaybackRedirect = () => {
  const { search, hash } = useLocation()
  return <Navigate to={{ pathname: ROAS_PAYBACK_PATH, search, hash }} replace />
}

const App = () => {
  // 静默清理缓存的逻辑
  const { trigger: clearCacheTrigger } = useSWRMutation(
    '/cache/clear',
    postFetcher,
  )

  useEffect(() => {
    // 页面加载时检查是否需要清理缓存
    const checkAndClearCache = async () => {
      if (shouldClearCache()) {
        try {
          console.log('检测到需要清理缓存，开始静默清理...')
          await clearCache(clearCacheTrigger, true)
          console.log('静默清理缓存完成')
        } catch (error) {
          // 静默清理失败时不显示错误提示，只记录日志
          console.error('静默清理缓存失败:', error)
        }
      }
    }

    checkAndClearCache()
  }, [clearCacheTrigger])

  return (
    <BrowserRouter>
      <Nav />
      <div className="container p-1">
        <Routes>
          <Route path="/" element={<RequireAuthLayout />}>
            <Route index element={<Welcome />} />
            <Route path="roi" element={<RoiLayout />}>
              <Route index element={<RoiHome />} />
              <Route path="neku-regression" element={<AppleRegression />} />
              <Route path="country-daily" element={<RoiCountryDailyLayout />}>
                <Route index element={<RoiCDRevenue />} />
              </Route>
              <Route
                path="cc/:pids/:date"
                element={<RoiCountryDetail />}
              ></Route>
              <Route path="pc" element={<RoiPCLayout />}>
                <Route index element={<RoiPCSummary />} />
                <Route path="cost" element={<RoiPCCost />} />
                <Route path="profit" element={<RoiPCProfit />} />
                <Route path="revenue" element={<RoiPCRevenue />} />
              </Route>
              <Route path="cc" element={<RoiCCLayout />}>
                <Route index element={<RoiCCRevenue />} />
                <Route path="cost" element={<RoiCCCost />} />
                <Route path="profit" element={<RoiCCProfit />} />
              </Route>
              <Route path="weekly" element={<RoiWeekly />} />
              <Route path="weekdoc" element={<RoiWeeklyDoc />} />
              <Route path="monthly" element={<RoiMonthly />} />
              <Route path="monthly/goals" element={<MonthlyGoals />} />
              <Route path="jpgroup" element={<RoiJPGroup />} />
              <Route path="apple/regression" element={<AppleRegression />} />
            </Route>

            <Route path="apple" element={<AppleLayout />}>
              <Route path="" element={<AppleEvents />} />
              <Route path="events1" element={<AppleEvents />} />
              <Route
                path="events1/:eventAt/roadmap"
                element={<Event1Roadmap />}
              />
              <Route path="events2" element={<AppleEvents2 />} />
              <Route
                path="events2/:subAt/roadmap"
                element={<Event2Roadmap />}
              />
              <Route path="events3" element={<WhatHappenAfterSubAt />} />
              <Route path="subat-timeline" element={<SubAtTimeline />} />
              <Route path="subscribe-summary" element={<SubscribeSummary />} />
            </Route>
            <Route path="troas" element={<RoasLayout />}>
              <Route index element={<Navigate to="campaigns" replace />} />
              <Route path="roas" element={<RoasPage />} />
              <Route path="data-dashboard" element={<RoasDataDashboard />} />
              <Route path="campaigns" element={<CampsPage />} />
              <Route path="day0-roas" element={<Day0RoasPage />} />
              <Route path="day0-roas/14" element={<DayRoas14Page />} />
              <Route path="notes" element={<NotesPage />} />
              <Route path="campaigns/:id" element={<RoasDetail />} />
              <Route path="rs" element={<RevenueStructure />} />
              <Route path="rs-age" element={<RevenueAgeStructure />} />
              <Route path="summary" element={<RoasSummary />} />
              <Route path="report" element={<RoasReport />} />
              <Route path="report/detail" element={<RoasDetailReport />} />
              <Route path="revenues-snapshot" element={<RevenuesSnapshot />} />
              <Route path="recovery" element={<RecoveryModel />} />
            </Route>
            <Route path="roas" element={<RequireCeo />}>
              <Route index element={<Navigate to="payback" replace />} />
              <Route path="payback" element={<CeoPayback />} />
            </Route>
            <Route path="ceo" element={<RequireCeo />}>
              <Route index element={<LegacyPaybackRedirect />} />
              <Route path="payback" element={<LegacyPaybackRedirect />} />
            </Route>
            <Route path="baseline" element={<Baseline />} />
            <Route path="er" element={<EarningRate />} />
            <Route path="dr" element={<Day0RoasBaseline />} />
            <Route path="cost-baseline" element={<CostBaseline />} />
            <Route path="profit-baseline" element={<ProfitBaseline />} />
            <Route path="admob" element={<AdmobLayout />}>
              <Route index element={<AdmobReportPage />} />
              <Route path="pam" element={<PamIndex />}>
                <Route index element={<PamAutoImportPage />} />
                <Route path="create" element={<PamCreatePage />} />
              </Route>
            </Route>
            <Route path="admin" element={<RequireAdminLayout />}>
              <Route index element={<Navigate to="api-timing" replace />} />
              <Route path="api-timing" element={<ApiTiming />} />
              <Route path="users" element={<UsersPage />}>
                <Route path=":id" element={<UserLayout />}>
                  {userDetailRoutes}
                </Route>
              </Route>
              <Route path="bots" element={<BotsPage />}>
                <Route path=":id" element={<UserLayout />}>
                  {userDetailRoutes}
                </Route>
              </Route>
              <Route path="package-aliases" element={<PackageAliasPage />} />
              <Route path="product-tree" element={<ProductTreePage />} />
            </Route>
          </Route>
          <Route path="login" element={<LoginPage />} />
          <Route path="401" element={<Page401 />} />
          <Route path="403" element={<Page403 />} />
          <Route path="500" element={<Page500 />} />
          <Route path="*" element={<Page404 />} />
        </Routes>
        <Toaster />
      </div>
    </BrowserRouter>
  )
}

/** 用户 / bot 详情共用的权限配置子路由 */
const userDetailRoutes = (
  <>
    <Route path="camproi" element={<SetCamproiReadable />} />
    <Route path="roas" element={<SetRoasReadable />} />
    <Route path="revenue" element={<SetRevenueReadable />} />
    <Route path="cost" element={<SetCostReadable />} />
    <Route path="pr" element={<SetPrReadable />} />
    <Route path="admob" element={<SetAdmobReadable />} />
    <Route path="pam" element={<SetPamReadable />} />
    <Route path="roles" element={<SetRoles />} />
  </>
)

const RequireAuthLayout = () => {
  const isLoggedIn = useAuthStore((state) => state.auth?.id)
  const location = useLocation()
  if (isLoggedIn) {
    return (
      <>
        <ProductPackagesLoader />
        <ProductTreeLoader>
          <Outlet />
        </ProductTreeLoader>
      </>
    )
  }
  return <Navigate to="/login" state={{ from: location }} replace />
}

const RequireAdminLayout = () => {
  const hasRole = useRoles([Role.Admin, Role.BuyerLeader, Role.RevenuerLeader])
  if (!hasRole) return <Navigate to="/403" />
  return <AdminLayout />
}

export default App
